import {
  IQuoteTemplateConfigSchema,
  QuoteAmendmentVersionEnum,
  QuoteStatusEnum,
  ToggleState,
} from '@monetize/types/app';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  useGetQuoteDisplayConfig,
  useGetQuoteStorageById,
  useUpdateQuoteDisplayConfig,
} from '../../../../api/cpqService';
import {
  MAccordion,
  MBox,
  MFlex,
  MGrid,
  MGridItem,
  MPageLoader,
  MSkeleton,
  MText,
} from '../../../../components/Monetize';
import { DeltaviewFailureExplanation } from '../../../../components/Quotes/DeltaviewFailureExplanation';
import { QuoteTemplateForm } from '../../../../components/Quotes/QuoteTemplates/QuoteTemplateForm';
import { useQuoteTemplateCustomizationAccess } from '../../../../components/Quotes/QuoteTemplates/useQuoteTemplateSettingVisibility';
import { useDocumentHead } from '../../../../services/documentHead';
import { ensureBoolean } from '../../../../utils';
import { createInFlightRequests } from '../../../../utils/inFlightRequests';
import { QuoteConfetti } from '../../Quote/components/QuoteConfetti';
import { useQuotePdfDrawer } from '../../Quote/hooks/useQuotePdfDrawer';
import { useQuoteContext } from '../../Quote/quoteContext';
import { getStrToRerenderQuote } from '../../Quote/quoteUtils';
import { QuoteBasic } from './QuoteBasic';
import { QuotePageCustomerBar } from './QuotePageCustomerBar';
import QuotePDFDisplay from './QuotePDFDisplay';

const DEFAULT_ZOOM = 0.9;
const BAR_HEIGHT = 64;

interface QuotePageCustomerProps {
  showQuoteInformation: boolean;
  showDownloadButton: boolean;
  isExternalView: boolean;
  customizePdfFormDisabled: boolean;
  onForceQuoteRefresh?: () => void;
  headerHeight?: number;
}
export const QuotePageCustomer = ({
  showQuoteInformation,
  showDownloadButton,
  isExternalView,
  customizePdfFormDisabled,
  onForceQuoteRefresh,
  headerHeight = 0,
}: QuotePageCustomerProps) => {
  const {
    quoteData: { quote, displayConfig },
    setReviewQuoteDisplayWidth,
  } = useQuoteContext();
  const isAmendmentV2Quote =
    quote?.amendmentVersion === QuoteAmendmentVersionEnum.v2;
  // When every customization setting is hidden for the user's roles, the
  // Customize Settings button and sidebar are not rendered at all.
  const { hasAnyVisibleSetting } =
    useQuoteTemplateCustomizationAccess(isAmendmentV2Quote);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerOffset, setContainerOffset] = useState(0);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  const { data: quoteDisplayConfig } = useGetQuoteDisplayConfig(quote?.id!, {
    enabled: !!quote?.id,
  });

  const reviewQuoteRef = useRef<any>();
  const [searchParams] = useSearchParams();
  const isCelebrate = searchParams.get('celebrate');
  const { data: document } = useGetQuoteStorageById(
    {
      quoteId: quote?.id!,
      storageId: quote?.documentLocation!,
      params: { attachContent: true },
    },
    { enabled: !!quote?.id && !!quote?.documentLocation },
  );
  const [isSettingsUpdated, setIsSettingsUpdated] = useState(false);
  const [documents, setDocuments] = useState<{
    [key: string]: ArrayBuffer | string | null;
  }>({});
  const [renderKey, setRenderKey] = useState(() => new Date().getTime());

  const { tabs, defaultTab, resetTabs, fetchDocument } = useQuotePdfDrawer(
    quote ?? {},
  );

  const [loading, setLoading] = useState(false);
  const [isRefetchingDocument, setIsRefetchingDocument] = useState(false);

  const [currentPdfFile, setCurrentPdfFile] = useState<
    ArrayBuffer | Blob | string | null
  >();

  const [currentTabIndex, setCurrentTabIndex] = useState<number>(
    quote?.status === QuoteStatusEnum.PROCESSED ? defaultTab : 0,
  );
  const hasUserSelectedTabRef = useRef(false);

  useEffect(() => {
    if (
      quote?.status === QuoteStatusEnum.PROCESSED &&
      !hasUserSelectedTabRef.current
    ) {
      setCurrentTabIndex(defaultTab);
    }
  }, [defaultTab, quote?.status]);

  useEffect(() => {
    if (tabs.length > 0) {
      hasUserSelectedTabRef.current = false;
      setCurrentTabIndex(defaultTab);
    }
  }, [tabs.length, defaultTab]);

  const currentTabValue = tabs?.length
    ? tabs[Math.min(currentTabIndex, tabs.length - 1)].value
    : 'blank';

  const { mutate: doUpdateQuoteDisplayConfig } = useUpdateQuoteDisplayConfig({
    onSuccess: () => {
      setIsSettingsUpdated(false);
    },
    onError: (err) => {
      handleApiErrorToast(err);
      setIsSettingsUpdated(false);
    },
  });

  const [externalPdfDrawer, setExternalPdfDrawer] = useState<ToggleState>(
    ToggleState.OPEN,
  );

  useEffect(() => {
    if (isExternalView) {
      setExternalPdfDrawer(ToggleState.CLOSED);
    }
  }, [isExternalView]);

  const formState = useForm<IQuoteTemplateConfigSchema>({
    mode: 'onChange',
    defaultValues: {
      ...quoteDisplayConfig,
    },
  });

  const { handleSubmit, watch } = formState;

  const onSubmit = useCallback(
    async (requestData: IQuoteTemplateConfigSchema) => {
      if (!quote?.id) {
        return;
      }
      doUpdateQuoteDisplayConfig({ quoteId: quote.id, payload: requestData });
    },
    [doUpdateQuoteDisplayConfig, quote?.id],
  );

  const allValues = watch();

  useEffect(() => {
    const debouncedUpdate = debounce(() => {
      setIsSettingsUpdated(true);
      handleSubmit(onSubmit)();
    }, 1500);

    if (!isEqual(quoteDisplayConfig, allValues)) {
      setIsSettingsUpdated(true);
      debouncedUpdate();
    }

    // Cleanup debounced function
    return () => {
      debouncedUpdate.cancel();
    };
  }, [allValues, handleSubmit, onSubmit, quoteDisplayConfig]);

  // NOTE: Leave these setDocTitle within the index.tsx and QuotePageCustomer.tsx
  const { setDocTitle } = useDocumentHead();
  useEffect(() => {
    setDocTitle(
      `Review Quote${quote?.description ? ` - ${quote.description}` : ''}`,
    );
  }, [quote?.description, setDocTitle]);

  useEffect(() => {
    if (reviewQuoteRef.current) {
      // Calculating review quote display width and removing padding
      setReviewQuoteDisplayWidth(reviewQuoteRef.current.clientWidth - 64);
    }
  }, [
    reviewQuoteRef,
    document?.contentBase64Encoded,
    setReviewQuoteDisplayWidth,
  ]);

  useEffect(() => {
    resetTabs();
  }, [resetTabs]);

  useEffect(() => {
    if (!isExternalView && containerRef.current) {
      let rafId: number | null = null;

      const updateOffset = () => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          setContainerOffset(rect.top);
        }
        rafId = null;
      };

      const throttledUpdate = () => {
        if (rafId === null) {
          rafId = requestAnimationFrame(updateOffset);
        }
      };
      rafId = requestAnimationFrame(updateOffset);

      window.addEventListener('scroll', throttledUpdate, { passive: true });
      window.addEventListener('resize', throttledUpdate);

      return () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        window.removeEventListener('scroll', throttledUpdate);
        window.removeEventListener('resize', throttledUpdate);
      };
    } else if (isExternalView) {
      setContainerOffset(0);
    }
  }, [isExternalView]);

  const prevQuoteStrToRerenderRef = useRef<any>(null);
  // Coalesces concurrent document requests per tab. The loader below re-runs
  // whenever `quote` gets a new reference, which happens repeatedly while a slow
  // `attachContent` request is still pending — without this, each re-run would
  // fire a duplicate API call for the same document.
  const [inFlightDocuments] = useState(() =>
    createInFlightRequests<ArrayBuffer | string | null>(),
  );
  // `displayConfig`/`quote`/`documents` are all effect dependencies below, and
  // any one of them can resolve (for reasons unrelated to the print content
  // itself) while a print fetch is still in flight for a *different* reason.
  // An effect-instance-scoped `isCancelled` would wrongly treat that as "a
  // newer fetch took over" and discard this fetch's result even though
  // nothing else is going to. Only a real unmount should suppress the update.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const inFlight = inFlightDocuments;

    const newQuoteStrToRerender = getStrToRerenderQuote(quote, displayConfig);
    const shouldForceRefetch =
      (currentTabValue === 'QUOTE' || currentTabValue === 'FULL_QUOTE') &&
      prevQuoteStrToRerenderRef.current !== newQuoteStrToRerender;

    const isDocumentCached = !!documents[currentTabValue];
    // The quote keeps getting touched by background backend work for a
    // while after status transitions (e.g. approval -> send -> process each
    // writing separately), which repeatedly flips shouldForceRefetch. Once we
    // already have content to show, a re-fetch triggered by one of those
    // touches must not blank the current preview or show the skeleton again
    // — only the very first load for a tab does that.
    const isFirstLoad = !isDocumentCached;

    const loadDocument = async () => {
      if (isFirstLoad) {
        setLoading(true);
      } else {
        setIsRefetchingDocument(true);
      }
      try {
        // inFlight coalesces concurrent calls for this tab into a single
        // request, so there is at most one such request outstanding at a
        // time - it's always safe to apply its result once it settles.
        const pdfDocument = await inFlight.run(currentTabValue, () =>
          fetchDocument(currentTabValue),
        );
        if (!isMountedRef.current) {
          return;
        }

        setDocuments((prev) => ({
          ...prev,
          [currentTabValue]: pdfDocument || null,
        }));
      } catch (ex) {
        if (isMountedRef.current) {
          handleApiErrorToast(ex);
          if (isFirstLoad) {
            setCurrentPdfFile(null);
          }
        }
      } finally {
        if (isMountedRef.current) {
          if (isFirstLoad) {
            setLoading(false);
          } else {
            setIsRefetchingDocument(false);
          }
        }
      }
    };

    if (shouldForceRefetch || !isDocumentCached) {
      loadDocument();
    }

    // Update ref after comparison
    prevQuoteStrToRerenderRef.current = newQuoteStrToRerender;
  }, [
    currentTabValue,
    displayConfig,
    documents,
    fetchDocument,
    inFlightDocuments,
    quote,
  ]);

  useEffect(() => {
    const document = documents[currentTabValue];
    if (!document) {
      return;
    }

    if (typeof document === 'string') {
      // HTML content can update in place - the iframe just gets a new
      // srcDoc, no remount needed. Forcing one here (as the PDF branch below
      // does) would reset HtmlIFrameContentRenderer's own loading state and
      // flash the skeleton every time a background quote touch re-fetches it.
      setCurrentPdfFile(document);
      // Switching here from a PDF tab cancels that tab's pending Blob swap via
      // the cleanup below, and the timer it cancels is what would have cleared
      // `loading`. This tab's document is already cached, so nothing else will
      // clear it and the page would sit on the skeleton forever.
      setLoading(false);
      return;
    }

    // react-pdf transfers the array buffer to the worker, so we need to
    // clone it (via a fresh Blob + remount) so we can continue to use it.
    setCurrentPdfFile(null);
    setRenderKey(new Date().getTime());
    setLoading(true);
    // `documents` gets a new identity every time the loader above stores a
    // result, so this effect re-runs while an earlier timer is still pending.
    // Without the cleanup those timers overlap: each one swaps in its own Blob
    // and bumps `renderKey`, remounting react-pdf's <Document> while the
    // previous one's asynchronous `loadingTask.destroy()` is still in flight.
    // A <Page> that calls `pdf.getPage()` in that window hits a transport
    // whose `messageHandler` has already been nulled, and pdf.js throws
    // synchronously out of an effect body - straight past react-pdf's
    // `onLoadError` and into the global error boundary.
    const timeoutId = setTimeout(() => {
      try {
        setCurrentPdfFile(new Blob([document]));
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [currentTabValue, documents]);

  const handleTabChange = (index: number) => {
    hasUserSelectedTabRef.current = true;
    setCurrentTabIndex(index);
    setExternalPdfDrawer(ToggleState.CLOSED);
    setRenderKey(new Date().getTime());
    setZoom(DEFAULT_ZOOM);
  };

  if (!quote || !displayConfig) {
    return <MPageLoader />;
  }

  const isExternalPDFDrawerOpen =
    externalPdfDrawer === ToggleState.OPEN && hasAnyVisibleSetting;

  return (
    <>
      {showQuoteInformation && (
        <>
          <QuoteConfetti active={ensureBoolean(isCelebrate)} />
          <MAccordion
            w="100%"
            my={4}
            collapsible
            variant="outline"
            defaultValue={['0']}
          >
            <QuoteBasic
              value="0"
              onModifiedAccount={() => onForceQuoteRefresh?.()}
            />
          </MAccordion>
        </>
      )}
      <MBox
        ref={containerRef}
        minH="75vh"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="border.default"
        borderTopRadius="md"
      >
        {!!quote && (
          <QuotePageCustomerBar
            quote={quote}
            pdfDrawerConfig={{
              externalPdfDrawer,
              setExternalPdfDrawer,
            }}
            showDownloadButton={showDownloadButton}
            showCustomizeButton={hasAnyVisibleSetting}
            tabs={tabs}
            currentTabIndex={currentTabIndex}
            currentTabValue={currentTabValue}
            handleTabChange={handleTabChange}
            zoom={zoom}
            setZoom={setZoom}
            isExternalView={isExternalView}
            headerHeight={headerHeight}
            containerOffset={containerOffset}
          />
        )}

        <DeltaviewFailureExplanation
          quote={quote}
          displayConfig={displayConfig}
        />

        <MGrid templateColumns="repeat(6, 1fr)" gap={4}>
          <MGridItem colSpan={isExternalPDFDrawerOpen ? 4 : 6}>
            {loading && LOADING_SKELETON}
            {!loading && !!quote && !!displayConfig && (
              <QuotePDFDisplay
                quote={quote}
                currentTabIndex={currentTabIndex}
                reviewQuoteRef={reviewQuoteRef}
                renderKey={renderKey}
                isReFetching={isSettingsUpdated || isRefetchingDocument}
                currentPdfFile={currentPdfFile}
                zoom={zoom}
                height="90vh"
              />
            )}
          </MGridItem>

          {isExternalPDFDrawerOpen && (
            <MGridItem
              colSpan={isExternalPDFDrawerOpen ? 2 : 0}
              padding={4}
              position="sticky"
              top={`${isExternalView ? headerHeight + BAR_HEIGHT : Math.max(0, headerHeight + BAR_HEIGHT - containerOffset + 16)}px`}
              alignSelf="start"
              height="fit-content"
              zIndex={2}
            >
              <MText color="tGray.darkPurple">
                These settings only apply to the review page and the PDF file.
                Download the PDF to preview the settings.
              </MText>
              <QuoteTemplateForm
                source="quote"
                formState={formState}
                isDisabled={customizePdfFormDisabled}
                requireSigningPage={quote.requiresEsign}
                isAmendmentV2Quote={isAmendmentV2Quote}
              />
            </MGridItem>
          )}
        </MGrid>
      </MBox>
    </>
  );
};

const LOADING_SKELETON = (
  <MBox padding="6" w="100%">
    <MSkeleton height="25px" width="20%" />
    <MSkeleton height="12px" width="10%" mt="1" />
    <MSkeleton height="12px" width="15%" mt="1" />
    <MFlex alignItems="center" justifyContent="space-between" mt="10" w="100%">
      <MBox w="50%">
        <MSkeleton height="15px" width="30%" />
        <MSkeleton height="15px" width="35%" mt="1" />
        <MSkeleton height="15px" width="20%" mt="1" />
        <MSkeleton height="15px" width="25%" mt="1" />
      </MBox>
      <MBox w="50%" textAlign="right" display="flex" justifyContent="flex-end">
        <MSkeleton height="80px" width="50%" />
      </MBox>
    </MFlex>
  </MBox>
);
